export interface ParsedTitle {
  artist: string;
  track: string;
}

const SEPARATOR = ' - ';

/** Regex to match trailing decorations like "(Official Video)" or "[HD]".
 *  Decoration is anything in (...) or [...] at the very end of the string.
 *  Allows multiple consecutive decorations (e.g. "(HD) [Official]"). */
const DECORATION_RE = /\s*[([][^\])]*[\])]\s*$/;

/** Decorations we keep because they're meaningful to the track identity. */
const KEEP_PREFIXES = ['feat', 'ft.', 'ft ', 'with ', 'remix', 'original', 'extended'];

function shouldKeep(decorationInner: string): boolean {
  const lower = decorationInner.trim().toLowerCase();
  return KEEP_PREFIXES.some((p) => lower.startsWith(p));
}

function stripTrailingDecorations(s: string): string {
  let current = s.trim();
  while (true) {
    const m = current.match(DECORATION_RE);
    if (m === null) break;
    const matched = m[0].trim();
    // Extract the inner content (skip the bracket chars)
    const inner = matched.slice(1, -1);
    if (shouldKeep(inner)) break;
    current = current.slice(0, current.length - matched.length).trim();
  }
  return current;
}

/**
 * Parse a raw title like "Artist - Track (Official Video)" into
 * { artist, track }. Returns null if no clear separator.
 */
export function parseTitle(raw: string): ParsedTitle | null {
  if (!raw || raw.trim() === '') return null;
  const idx = raw.indexOf(SEPARATOR);
  if (idx === -1) return null;
  const artist = raw.slice(0, idx).trim();
  const trackRaw = raw.slice(idx + SEPARATOR.length).trim();
  if (artist === '' || trackRaw === '') return null;
  const track = stripTrailingDecorations(trackRaw);
  if (track === '') return null;
  return { artist, track };
}
